import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';

const serviceLinks = [
  { label: 'New Construction Wiring', href: '/services/new-construction' },
  { label: 'Residential Service & Repair', href: '/services/residential-repair' },
  { label: 'Generators', href: '/services/generators' },
  { label: 'Electric Car Chargers', href: '/services/ev-chargers' },
  { label: 'Solar & Panel Upgrades', href: '/services/solar-panel' },
  { label: 'Specialty Lighting', href: '/services/specialty-lighting' },
];

const ThemeToggle = () => {
  const { theme, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      aria-label="Toggle light/dark mode"
      title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      className={`relative w-[52px] h-[28px] rounded-full transition-all duration-400 cursor-pointer flex-shrink-0 border-2 ${
        theme === 'light'
          ? 'bg-amber-400 border-amber-300 shadow-[0_0_12px_rgba(251,191,36,0.4)]'
          : 'bg-[#0D1E35] border-[#1E3048]'
      }`}
    >
      <span
        className={`absolute top-[3px] w-[18px] h-[18px] rounded-full flex items-center justify-center transition-all duration-300 shadow-md ${
          theme === 'light'
            ? 'translate-x-[26px] bg-white'
            : 'translate-x-[3px] bg-[#1A3055]'
        }`}
      >
        <i
          className={`text-[9px] ${
            theme === 'light' ? 'ri-sun-fill text-amber-500' : 'ri-moon-fill text-[#00D9FF]'
          }`}
        />
      </span>
    </button>
  );
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 5);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const scrolledBg =
    theme === 'light'
      ? 'bg-white border-b border-gray-200 shadow-[0_2px_12px_rgba(0,0,0,0.07)]'
      : scrolled
      ? 'bg-[#0A1628]/96 backdrop-blur-md border-b border-[#00D9FF]/15 shadow-[0_4px_32px_rgba(0,0,0,0.5)]'
      : 'bg-transparent';

  const isLightScrolled = theme === 'light';

  const linkClass = `relative px-4 py-2 text-[13px] font-medium tracking-widest uppercase transition-colors duration-300 group cursor-pointer ${
    isLightScrolled ? 'text-[#1E3048]/80 hover:text-[#0A1628]' : 'text-white/80 hover:text-white'
  }`;

  const activeLinkClass = `relative px-4 py-2 text-[13px] font-medium tracking-widest uppercase transition-colors duration-300 group cursor-pointer text-[#00D9FF]`;

  const dropdownBg = 'bg-white border-gray-200 shadow-[0_16px_48px_rgba(0,0,0,0.12)]';
  const dropdownLinkClass = 'text-[#1E3048]/70 hover:text-[#0A1628] hover:bg-gray-50';

  const mobileBg =
    theme === 'light'
      ? 'bg-[#F4F8FC]/98 backdrop-blur-lg border-t border-[#C4D8EC]'
      : 'bg-[#0A1628]/98 backdrop-blur-lg border-t border-[#00D9FF]/10';

  const mobileLinkClass =
    theme === 'light'
      ? 'text-[#1E3048]/80 hover:text-[#0A1628] border-b border-[#C4D8EC]'
      : 'text-white/80 hover:text-[#00D9FF] border-b border-white/5';

  const phoneClass =
    isLightScrolled
      ? 'text-[#1E3048]/70 hover:text-[#0A1628]'
      : 'text-white/70 hover:text-[#00D9FF]';

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${scrolledBg}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-[140px]">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <img
            src={
              theme === 'light'
                ? 'https://storage.readdy-site.link/project_files/e8b1354e-27d4-4a3e-a932-cdbda977ab1c/61b1c158-b125-4805-92e3-f16c8a46475d_logo-blue.png?v=736bce924784d7c2550ae960d6719cc9'
                : 'https://static.readdy.ai/image/577c45d6defa6ac02f4bf39c73856fa0/a10a21fb6a7818eb13553848461bbd4a.png'
            }
            alt="DOC Electric LLC"
            className="h-[130px] w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          <Link to="/" className={activeLinkClass}>
            Home
            <span className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#00D9FF] rounded-full" />
          </Link>

          {/* Services Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              className={`${linkClass} flex items-center gap-1.5`}
              onClick={() => setServicesOpen(!servicesOpen)}
            >
              Services
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
              </span>
              <span className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#00D9FF] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out rounded-full" />
            </button>

            <div
              className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 border rounded-xl overflow-hidden transition-all duration-200 ${dropdownBg} ${
                servicesOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'
              }`}
            >
              <div className="p-2">
                {serviceLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setServicesOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-[13px] font-medium transition-all duration-200 group cursor-pointer ${dropdownLinkClass}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00D9FF]/40 group-hover:bg-[#00D9FF] flex-shrink-0 transition-colors duration-200" />
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <Link to="/about" className={linkClass}>
            About
            <span className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#00D9FF] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out rounded-full" />
          </Link>

          <Link to="/reviews" className={linkClass}>
            Reviews
            <span className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#00D9FF] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out rounded-full" />
          </Link>

          <Link to="/contact" className={linkClass}>
            Contact
            <span className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#00D9FF] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out rounded-full" />
          </Link>
        </nav>

        {/* Desktop right */}
        <div className="hidden md:flex items-center gap-4">
          <ThemeToggle />
          <a
            href="tel:+12693700765"
            className={`text-[13px] font-medium transition-colors duration-300 flex items-center gap-2 cursor-pointer whitespace-nowrap ${phoneClass}`}
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-fill text-[#00D9FF] text-sm" />
            </span>
            (269) 370-0765
          </a>
          <a
            href="#contact"
            className="px-5 py-2.5 bg-[#00D9FF] hover:bg-[#00bde0] text-[#0A1628] font-bold text-[13px] uppercase tracking-wider rounded-lg transition-all duration-300 hover:shadow-[0_0_24px_rgba(0,217,255,0.45)] hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
          >
            Free Estimate
          </a>
        </div>

        {/* Mobile hamburger */}
        <div className="md:hidden flex items-center gap-3">
          <ThemeToggle />
          <button
            className={`w-10 h-10 flex items-center justify-center cursor-pointer ${isLightScrolled ? 'text-[#0A1628]' : 'text-white/90'}`}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ${
          mobileOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
        } ${mobileBg}`}
      >
        <div className="px-6 py-4 flex flex-col gap-1">
          <Link
            to="/"
            onClick={() => setMobileOpen(false)}
            className={`py-3 text-[13px] font-medium tracking-widest uppercase text-[#00D9FF] border-b ${
              theme === 'light' ? 'border-[#C4D8EC]' : 'border-white/5'
            } transition-colors duration-200 cursor-pointer`}
          >
            Home
          </Link>

          <div className={`border-b ${theme === 'light' ? 'border-[#C4D8EC]' : 'border-white/5'}`}>
            <button
              className={`w-full py-3 text-[13px] font-medium tracking-widest uppercase flex items-center justify-between cursor-pointer transition-colors duration-200 ${
                theme === 'light' ? 'text-[#1E3048]/80 hover:text-[#0A1628]' : 'text-white/80 hover:text-[#00D9FF]'
              }`}
              onClick={() => setServicesOpen(!servicesOpen)}
            >
              Services
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
            </button>
            {servicesOpen && (
              <div className="pl-4 pb-2 flex flex-col gap-1">
                {serviceLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => { setMobileOpen(false); setServicesOpen(false); }}
                    className={`py-2.5 text-[12px] font-medium transition-colors duration-200 cursor-pointer ${
                      theme === 'light' ? 'text-[#1E3048]/60 hover:text-[#0A1628]' : 'text-white/60 hover:text-[#00D9FF]'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link to="/about" onClick={() => setMobileOpen(false)} className={`py-3 text-[13px] font-medium tracking-widest uppercase ${mobileLinkClass} transition-colors duration-200 cursor-pointer`}>About</Link>
          <Link to="/reviews" onClick={() => setMobileOpen(false)} className={`py-3 text-[13px] font-medium tracking-widest uppercase ${mobileLinkClass} transition-colors duration-200 cursor-pointer`}>Reviews</Link>
          <Link to="/contact" onClick={() => setMobileOpen(false)} className={`py-3 text-[13px] font-medium tracking-widest uppercase ${mobileLinkClass} transition-colors duration-200 cursor-pointer`}>Contact</Link>

          <a
            href="#contact"
            onClick={() => setMobileOpen(false)}
            className="mt-3 py-3 bg-[#00D9FF] text-[#0A1628] font-bold text-center text-[13px] uppercase tracking-wider rounded-lg cursor-pointer whitespace-nowrap"
          >
            Request Free Estimate
          </a>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
